import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * Cart drawer component.
 *
 * OzFDM change: when the drawer auto-opens after an add-to-cart and the shopper closes it before the
 * add has fully settled (sections re-rendered, "Complete your setup" refreshed), the stock theme
 * re-opened it once the add promise resolved. We now remember that the shopper dismissed it and
 * leave it closed.
 */
class CartDrawerComponent extends Component {
  #summaryThreshold = 0.5;
  #dismissedSinceAdd = false;

  get #themeDrawer() {
    return this.closest('theme-drawer');
  }

  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.addEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);
    this.#themeDrawer?.hasAttribute('open') && this.#handleDrawerOpen();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.removeEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);
  }

  #handleDrawerOpen = () => {
    this.#updateStickyState();
    customElements.whenDefined('shopify-payment-terms').then(() => {
      this.querySelector('shopify-payment-terms')
        ?.shadowRoot?.querySelector('#shopify-installments-cta')
        ?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  #handleDrawerClose = () => {
    this.#dismissedSinceAdd = true;
  };

  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;
    const sourceModal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;

    this.#dismissedSinceAdd = false;
    shouldAutoOpen && !sourceModal && !this.#isCartEmpty() && this.#themeDrawer?.open();

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          // Only re-open if the shopper hasn't closed the drawer in the meantime.
          if (!this.#dismissedSinceAdd) {
            this.#themeDrawer?.isOpen || this.#themeDrawer?.open();
          }
          settle();
        };

        sourceModal?.open ? sourceModal.addEventListener('close', openAndSettle, { once: true }) : openAndSettle();
      })
      .catch((error) => {
        error?.name !== 'AbortError' && console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return !!this.querySelector('.cart-drawer--empty');
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const ratio = summary.getBoundingClientRect().height / drawerHeight;

    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

customElements.get('cart-drawer-component') || customElements.define('cart-drawer-component', CartDrawerComponent);
